import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { appConfig } from "../hooks/useConfig";
import { API_URL } from "./constants";
import { RootState } from "../store";

type AddSurgeonAddressInput = {
  phone?: string | null;
  provider_number?: string | null;
  fax?: string | null;
  mail?: string | null;
  unit_number?: string | null;
  street_name?: string | null;
  street_number?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
};

// specimen audit / tracking — shared request & response contracts.
// Field names here mirror Lambda-Admin's SpecimenAuditFilters / *Out schemas
// (src/specimen_audit.py, schema/specimen_audit.py) — keep them in sync.
export type SpecimenAuditSeverity = "normal" | "warning" | "breach";

// health_org_id is a URL path segment server-side (tenant schema routing),
// not a query param — kept separate from the filter params below.
export type SpecimenAuditFilterParams = {
  from_datetime?: string;
  to_datetime?: string;
  search?: string;
  specimen_id?: number;
  current_status?: string;
  has_discrepancy?: boolean;
  severity?: SpecimenAuditSeverity;
  sort?: string;
};

export type SpecimenAuditEventsParams = SpecimenAuditFilterParams & {
  healthOrgId: number;
  page?: number;
  page_size?: number;
};

export type SpecimenAuditAnalyticsParams = SpecimenAuditFilterParams & {
  healthOrgId: number;
};

// Raw shapes as returned by the API (SpecimenAuditEventOut / SpecimenAuditEventsOut).
export type SpecimenAuditEventRaw = {
  id: number;
  created_at: string;
  created_by?: string | null;
  comments?: string | null;
  note_by_author?: string | null;
  specimen_id?: number | null;
  specimen_qr?: string | null;
  hazard_bag_id?: number | null;
  hazard_bag_qr?: string | null;
  request_form_id?: number | null;
  request_form_qr?: string | null;
  specimen_previous_status?: string | null;
  specimen_current_status?: string | null;
  specimen_action?: string | null;
  hazard_bag_previous_status?: string | null;
  hazard_bag_current_status?: string | null;
  hazard_bag_action?: string | null;
  request_form_previous_status?: string | null;
  request_form_current_status?: string | null;
  request_form_action?: string | null;
  discrepancy_id?: number | null;
  discrepancy_reason?: string | null;
  has_evidence: boolean;
  evidence_url?: string | null;
  is_hidden_recalculation: boolean;
  severity: SpecimenAuditSeverity;
};

export type SpecimenAuditEventsRawResponse = {
  items: SpecimenAuditEventRaw[];
  count: number;
  page: number;
  page_size: number;
};

// FE display model — derived from SpecimenAuditEventRaw via transformResponse.
export type SpecimenAuditImpact = {
  entity: "specimen" | "hazard_bag" | "request_form";
  previous_status?: string | null;
  current_status?: string | null;
  action?: string | null;
};

export type SpecimenAuditEvent = {
  id: number | string;
  occurred_at: string;
  specimen_qr?: string | null;
  bag_qr?: string | null;
  request_form_id?: number | string | null;
  status_transition_from?: string | null;
  status_transition_to?: string | null;
  actor_email?: string | null;
  action?: string | null;
  reason?: string | null;
  comments?: string | null;
  evidence_url?: string | null;
  is_discrepancy?: boolean;
  severity: SpecimenAuditSeverity;
  is_hidden_recalculation?: boolean;
  impacts?: SpecimenAuditImpact[];
};

export type SpecimenAuditEventsResponse = {
  count: number;
  items: SpecimenAuditEvent[];
};

export type SpecimenAuditTransitionMatrixCell = {
  previous_status: string;
  current_status: string;
  count: number;
};

export type SpecimenAuditDiscrepancyReason = {
  reason: string;
  count: number;
};

export type SpecimenAuditSlaBreach = {
  specimen_id: number;
  specimen_qr?: string | null;
  lab_handoff_minutes: number;
  breach_by_minutes: number;
};

export type SpecimenAuditAnalyticsResponse = {
  total_status_changes: number;
  hidden_recalculation_count: number;
  discrepancy_count: number;
  discrepancy_rate: number;
  avg_lab_handoff_minutes: number | null;
  no_specimen_event_count: number;
  transition_matrix: SpecimenAuditTransitionMatrixCell[];
  top_discrepancy_reasons: SpecimenAuditDiscrepancyReason[];
  top_discrepancy_reasons_total: number;
  sla_breaches: SpecimenAuditSlaBreach[];
};

// The API tracks status transitions per-entity (specimen / hazard bag / request
// form) rather than as one flat "status_transition_from/to" pair. The specimen is
// the headline; the other two are surfaced as impacts for the timeline inspector.
const ENTITY_ORDER: SpecimenAuditImpact["entity"][] = [
  "specimen",
  "hazard_bag",
  "request_form",
];

const mapAuditEvent = (raw: SpecimenAuditEventRaw): SpecimenAuditEvent => {
  const impacts: SpecimenAuditImpact[] = ENTITY_ORDER.map((entity) => {
    const prefix = entity === "hazard_bag" ? "hazard_bag" : entity;
    const previous_status = (raw as any)[`${prefix}_previous_status`] ?? null;
    const current_status = (raw as any)[`${prefix}_current_status`] ?? null;
    const action = (raw as any)[`${prefix}_action`] ?? null;
    return { entity, previous_status, current_status, action };
  }).filter(
    (impact) =>
      impact.previous_status !== null || impact.current_status !== null,
  );

  // The headline transition is always the specimen's own — this is a specimen
  // audit trail, and letting a hazard bag / request form recalculation win the
  // headline mislabelled rows whose specimen never moved (PCP2-309). The other
  // two entities stay visible as impacts on the timeline inspector.
  const primaryTransition = {
    previous_status: raw.specimen_previous_status ?? null,
    current_status: raw.specimen_current_status ?? null,
    action: raw.specimen_action ?? null,
  };

  return {
    id: raw.id,
    occurred_at: raw.created_at,
    specimen_qr: raw.specimen_qr,
    bag_qr: raw.hazard_bag_qr,
    request_form_id: raw.request_form_id,
    status_transition_from: primaryTransition.previous_status,
    status_transition_to: primaryTransition.current_status,
    actor_email: raw.created_by,
    action: primaryTransition.action,
    reason: raw.discrepancy_reason,
    comments: raw.comments,
    evidence_url: raw.evidence_url,
    is_discrepancy: raw.discrepancy_id != null,
    severity: raw.severity,
    is_hidden_recalculation: raw.is_hidden_recalculation,
    impacts,
  };
};

const mapAuditEventsResponse = (
  raw: SpecimenAuditEventsRawResponse,
): SpecimenAuditEventsResponse => ({
  count: raw.count,
  items: raw.items.map(mapAuditEvent),
});

// createApi function to configure an API slice using RTK Query

export const api = createApi({
  // Configuration for the API slice
  reducerPath: "api",

  // Configuration for the base query function using fetchBaseQuery
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig?.API_BASE_URL,
    // credentials: "include",
    prepareHeaders: async (headers, { getState }) => {
      // Get the access token from local storage
      // const accessToken = getCookie("Pegasus_auth");
      const { accessToken } = (getState() as RootState).auth as any;
      if (accessToken) {
        // Set the 'Authorization' header with the access token for authenticated requests
        headers.set("authorization", `Bearer ${accessToken}`);
      }
    },
  }),
  tagTypes: [
    "HealthOrganisations",
    "HealthOrganisationDetails",
    "Laboratory",
    "LaboratoryDetails",
    "UserRoles",
    "UserGroups",
    "APIServices",
    "UserType",
    "UserRoleDetails",
    "Surgeons",
    "SurgeonDetails",
    "PatientSpecimenRecords",
    "PatientRequestForm",
    "PatientSpecimenRecordDetails",
    "PatientDetails",
    "RequestFormPdf",
    "Users",
    "UserDetails",
    "OperatingTheatres",
    "OperatingTheatreDetails",
    "Specialities",
    "SpecialityDetails",
    "ClinicalDiagrams",
    "ClinicalDiagramDetails",
    "TestTypes",
    "TestTypeDetails",
    "Procedures",
    "ProcedureDetails",
    "Organs",
    "Sites",
    "Lesions",
    "Metadata",
    "Attachments",
    "HazardBag",
    "ReferenceData",
    "RoleAPIServices",
    "SpecimenAudit",
  ],
  // Define API endpoints using the 'endpoints' property within the createApi configuration
  endpoints: (builder) => ({
    getUserByMail: builder.query<Users, string>({
      query: (mail = "") => ({
        url: `${API_URL.user}/mail/${mail}`,
        method: "GET",
      }),
      providesTags: (_) => ["UserDetails"],
    }),
    // 'login' endpoint configuration for handling user login
    login: builder.mutation<LoginResponse, LoginPayload>({
      query: (body) => ({
        url: API_URL.login,
        method: "POST",
        body,
      }),
    }),

    // 'two-factor-verification' endpoint configuration for handling two factor authentication
    twoFactorAuth: builder.mutation<TokenResponse, TwoFactorPayload>({
      query: (body) => ({
        url: API_URL.authentication,
        method: "POST",
        body,
      }),
    }),

    // 'forgot-password' endpoint configuration for handling forgot password
    forgotPassword: builder.mutation<{ message: string }, EmailType>({
      query: (body) => ({
        url: API_URL.forgotPassword,
        method: "POST",
        body,
      }),
    }),

    // 'reset-password' endpoint configuration for handling reset password
    resetPassword: builder.mutation<{ message: string }, ResetPasswordPayload>({
      query: (body) => ({
        url: API_URL.resetPassword,
        method: "POST",
        body,
      }),
    }),

    // 'change-password' endpoint configuration for handling change password
    changePassword: builder.mutation<
      { message: string },
      ChangePasswordPayload
    >({
      query: (body) => ({
        url: API_URL.changePassword,
        method: "POST",
        body,
      }),
    }),

    // 'refresh-token' endpoint configuration to get a new token from backend
    refreshToken: builder.mutation<TokenResponse, any>({
      query: (body) => ({
        url: API_URL.refreshToken,
        method: "POST",
        body,
      }),
    }),

    // 'logout' endpoint configuration for handling user logout
    logout: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.logout,
        method: "POST",
        body,
      }),
    }),

    // 'get health organisations'
    getHealthOrganisations: builder.query<any, any>({
      query: (param: any) => ({
        url: `${API_URL.getHealthOrgList}`,
        method: "GET",
        params: param,
      }),
      providesTags: (_) => ["HealthOrganisations"],
    }),

    // create health organisation
    createHealthOrganisation: builder.mutation<
      HealthOrganisation,
      AddHealthOrganisationPayload
    >({
      query: (body) => ({
        url: API_URL.addHealthOrg,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["HealthOrganisations"],
    }),

    // get selected health organisation details
    getHealthOrganisationDetails: builder.query<HealthOrganisation, string>({
      query: (id = "") => ({
        url: `${API_URL.getHealthOrgDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["HealthOrganisations", "HealthOrganisationDetails"],
    }),

    //get logged in user profile details
    getProfileDetails: builder.query<UserProfile, any>({
      query: () => ({
        url: API_URL.getProfileDetails,
        method: "GET",
      }),
    }),

    // get user list
    getUserList: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getUsers}${query}`,
        method: "GET",
      }),
      providesTags: (_) => ["Users"],
    }),

    //update health organisation details
    updateHealthOrganisationDetails: builder.mutation<
      any,
      EditHealthOrganisationPayload
    >({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateHealthOrgDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => [
        "HealthOrganisations",
        "HealthOrganisationDetails",
      ],
    }),

    // 'get laboratories'
    getLaboratories: builder.query<any, any>({
      query: (query = "") => ({
        url: API_URL.getLaboratoryList,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["Laboratory"],
    }),

    // create laboratory
    createLaboratory: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.addLaboratory,
        method: "POST",
        body,
        formData: true,
      }),
      invalidatesTags: (_) => ["Laboratory"],
    }),

    // get selected laboratory details
    getLaboratoryDetails: builder.query<Laboratory, string>({
      query: (id = "") => ({
        url: `${API_URL.getLaboratoryDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["LaboratoryDetails"],
    }),

    //update laboratory details
    updateLabDetails: builder.mutation<Laboratory, EditLaboratoryPayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateLaboratoryDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Laboratory", "LaboratoryDetails"],
    }),

    //get user roles
    getUserRoles: builder.query<
      ListResponse<UserRole>,
      Record<string, unknown> | void
    >({
      query: (query) => {
        const params = query ?? undefined;
        return {
          url: API_URL.userRoles,
          method: "GET",
          params,
        };
      },
      providesTags: (_) => ["UserRoles"],
    }),

    //get user group
    getUserGroup: builder.query<any, string>({
      query: (query = "") => ({
        url: `${API_URL.userGroups}${query}`,
        method: "GET",
      }),
      providesTags: (_) => ["UserGroups"],
    }),

    //get services
    getServices: builder.query<any, string>({
      query: (query = "") => ({
        url: `${API_URL.getAPIServiceList}`,
        method: "GET",
      }),
      providesTags: (_) => ["APIServices"],
    }),

    // create user role
    addUserRole: builder.mutation<any, AddUserRolePayload>({
      query: (body) => ({
        url: API_URL.userRole,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["UserRoles"],
    }),

    //get user type list
    getUserType: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.userType}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["UserType"],
    }),

    // get selected user role details
    getUserRoleDetails: builder.query<any, string>({
      query: (id = "") => ({
        url: `${API_URL.userRole}/id/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["UserRoleDetails"],
    }),

    //update user role status
    updateUserRoleStatus: builder.mutation<any, UpdateUserRoleStatusPayload>({
      query: (body) => ({
        url: API_URL.updateUserRoleStatus,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_) => ["UserRoles", "UserRoleDetails"],
    }),

    //update user role details
    updateUserRoleDetails: builder.mutation<any, UpdateUserRolePayload>({
      query: (body) => ({
        url: `${API_URL.updateUserRoleDetails}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => [
        "UserRoles",
        "UserRoleDetails",
        "RoleAPIServices",
        "APIServices",
      ],
    }),

    //create user admin role
    createUserAdmin: builder.mutation<CreateUserResponse, CreateUserPayload>({
      query: (body) => ({
        url: `${API_URL.createUserAdmin}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["UserGroups"],
    }),

    createUser: builder.mutation<CreateUserResponse, CreateUserPayload>({
      query: (body) => ({
        url: `${API_URL.createUserAdmin}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Users"],
    }),

    // 'get surgeons'
    getSurgeons: builder.query<
      ListResponse<Surgeon>,
      {
        search_key?: string;
        health_org_ids?: number;
        is_include_all_doctor?: boolean;
        is_detailed?: boolean;
        order_by_columns?: string;
        order?: string;
        doctor_id?: number;
        is_active?: boolean;
      }
    >({
      query: (query = {}) => ({
        url: `${API_URL.getSurgeons}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["Surgeons"],
    }),

    // get selected surgeon details
    getSurgeonDetails: builder.query<
      Surgeon,
      { id: string; healthOrgId: number }
    >({
      query: ({ id, healthOrgId }) => {
        const hasHealthOrg = Boolean(healthOrgId);
        const queryString = hasHealthOrg ? `?health_org_id=${healthOrgId}` : "";
        return {
          url: `${API_URL.surgeon}/${id}${queryString}`,
          method: "GET",
        };
      },
      providesTags: ["SurgeonDetails"],
    }),

    //update surgeon status
    updateSurgeonStatus: builder.mutation<any, UpdateSurgeonStatusPayload>({
      query: (body) => ({
        url: API_URL.updateSurgeonStatus,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_) => ["Surgeons"],
    }),

    // add surgeon
    addSurgeon: builder.mutation<
      Surgeon,
      {
        given_name: string;
        surname: string;
        prefix: string;
        speciality_id?: number;
        health_org_id?: number | null;
        is_associated?: boolean;
        address?: AddSurgeonAddressInput;
      }
    >({
      query: (body) => ({
        url: API_URL.addSurgeon,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Surgeons"],
    }),

    // update doctor via doctor API
    updateDoctor: builder.mutation<UpdateDoctorResponse, UpdateDoctorPayload>({
      query: ({ id, ...body }) => ({
        url: `${API_URL.updateDoctor}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_) => ["Surgeons"],
    }),

    // update surgeon
    updateSurgeon: builder.mutation<{ message: string }, EditSurgeonPayload>({
      query: (body) => ({
        url: `${API_URL.surgeon}/${body?.id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_) => ["Surgeons", "SurgeonDetails"],
    }),

    // manage surgeon default address
    updateSurgeonDefaultAddress: builder.mutation<
      any,
      UpdateDeliveryAddressPayload
    >({
      query: (body) => ({
        url: `doctor/${body?.doctor_id}/${API_URL.updateSurgeonDefaultAddress}?health_org_id=${body?.health_org_id}&default_delivery_address_id=${body?.doctor_delivery_address_id}&is_associated=${body?.is_associated}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_) => ["Surgeons", "SurgeonDetails"],
    }),

    addDoctorToHealthOrg: builder.mutation<
      Surgeon,
      { doctor_id: number; health_org_id: number }
    >({
      query: ({ doctor_id, health_org_id }) => ({
        url: `doctor/${doctor_id}/${API_URL.addDoctorToHealthOrg}?health_org_id=${health_org_id}`,
        method: "POST",
      }),
      invalidatesTags: (_) => ["Surgeons"],
    }),

    // get patient specimen records
    getPatientSpecimenRecords: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getPatientSpecimenRecords}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["PatientSpecimenRecords"],
    }),

    // get patient specimen records from pathology register (health org scoped)
    getPathologyRegisterPatientSpecimenRecords: builder.query<
      PatientRecord[],
      {
        healthOrgId: number;
        query?: PathologyRegisterPatientSpecimenRecordsQuery;
      }
    >({
      query: ({ healthOrgId, query }) => ({
        url: `pathology-register/${healthOrgId}/patient-specimen-records`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["PatientSpecimenRecords"],
    }),

    // get patient request form
    getPatientRequestForm: builder.query<
      any,
      { healthOrgId: number; id: number }
    >({
      query: ({ healthOrgId, id }) => {
        return {
          url: `${appConfig?.REQUEST_FORM_API_BASE_URL}health-org/${healthOrgId}/${API_URL.getPatientRequestForm}/${id}?is_detailed=true`,
          method: "GET",
        };
      },
      providesTags: (_) => ["PatientRequestForm"],
    }),

    // get patient specimen record details
    getPatientSpecimenRecordDetails: builder.query<PatientSpecimenRel, any>({
      query: (id = "") => ({
        url: `${API_URL.getPatientRequestFormDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["PatientSpecimenRecordDetails"],
    }),

    // get patient details
    getPatientDetails: builder.query<
      RequestFormResponse,
      { healthOrgId: number; patientId: number }
    >({
      query: ({ healthOrgId, patientId }) => ({
        url: `patient/id/${healthOrgId}/${patientId}`,
        method: "GET",
      }),
      providesTags: (_) => ["PatientDetails"],
    }),

    // fetch request form pdf via rf-api service
    getRequestFormPdf: builder.query<
      RequestFormResponse,
      { healthOrgId: number; requestFormId: number }
    >({
      query: ({ healthOrgId, requestFormId }) => {
        const baseDomain =
          appConfig?.API_BASE_URL?.replace(/\/api\/v2\/?$/, "") ||
          appConfig?.API_BASE_URL;
        return {
          url: `${baseDomain}/rf-api/v2/health-org/${healthOrgId}/request-form/get/${requestFormId}`,
          method: "GET",
        };
      },
      providesTags: (_) => ["RequestFormPdf"],
    }),

    // get users list
    getUsersList: builder.query<
      ListResponse<Users>,
      Record<string, unknown> | undefined
    >({
      query: (query) => ({
        url: `${API_URL.getUsers}`,
        method: "GET",
        params: query ?? undefined,
      }),
      providesTags: (_) => ["Users"],
    }),

    // get user details
    getUserDetails: builder.query<Users, any>({
      query: (id = "") => ({
        url: `${API_URL.user}/id/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["UserDetails"],
    }),

    // update users status
    updateUsersStatus: builder.mutation<any, UpdateUserStatusPayload>({
      query: (body) => ({
        url: API_URL.updateUsersStatus,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_) => ["Users", "UserDetails"],
    }),

    // create new user
    addUser: builder.mutation<
      { message: string },
      AddUserPayload | AddHospitalAdminPayload
    >({
      query: (body) => ({
        url: API_URL.user,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Users"],
    }),

    // update user
    updateUser: builder.mutation<
      { message: string },
      EditUserPayload | EditHospitalAdminPayload | UpdateUserStatusPayload
    >({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateUserDetails}/${id}`,
          method: "PATCH",
          body: rest,
        };
      },
      invalidatesTags: (_) => ["Users", "UserDetails"],
    }),

    // get operating theatres
    getOperatingTheatreList: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getOperatingTheatres}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["OperatingTheatres"],
    }),

    // update operating theatre status
    updateOperatingTheatreStatus: builder.mutation<
      any,
      UpdateOperatingTheatrePayload
    >({
      query: (body) => ({
        url: API_URL.updateOperatingTheatreStatus,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_) => ["OperatingTheatres", "OperatingTheatreDetails"],
    }),
    updateOperatingTheatreActivation: builder.mutation<
      { message: string },
      {
        id: number;
        is_active: boolean;
        deactivate_reason?: string;
        health_org_id?: number;
      }
    >({
      query: ({ id, ...rest }) => ({
        url: `${API_URL.updateOperatingTheatre}/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_) => ["OperatingTheatres", "OperatingTheatreDetails"],
    }),

    // get selected operating theatre details
    getOperatingTheatreDetails: builder.query<OperatingTheatre, string>({
      query: (id = "") => ({
        url: `${API_URL.operatingTheatre}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["OperatingTheatreDetails"],
    }),

    // create operating theatre
    addOperatingTheatre: builder.mutation<
      { message: string },
      AddOperatingTheatrePayload
    >({
      query: (body) => ({
        url: API_URL.addOperatingTheatre,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["OperatingTheatres"],
    }),

    // update operating theatre
    updateOperatingTheatre: builder.mutation<
      { message: string },
      EditOperatingTheatrePayload
    >({
      query: ({ id, ...rest }) => ({
        url: `${API_URL.updateOperatingTheatre}/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_) => ["OperatingTheatres", "OperatingTheatreDetails"],
    }),

    //delete cookies
    deleteCookies: builder.mutation<{ message: string }, any>({
      query: () => ({
        url: `${API_URL.deleteCookies}`,
        method: "POST",
      }),
    }),

    // get speciality list
    getSpecialities: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getSpecialityList}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["Specialities"],
    }),

    // get selected speciality details
    getSpecialityDetail: builder.query<Speciality, string>({
      query: (id = "") => ({
        url: `${API_URL.getSpecialityDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["SpecialityDetails"],
    }),

    //update speciality details
    updateSpecialityDetails: builder.mutation<any, EditSpecialityPayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateSpecialityDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Specialities", "SpecialityDetails"],
    }),

    // create speciality
    createSpeciality: builder.mutation<Speciality, AddSpecialityPayload>({
      query: (body) => ({
        url: API_URL.addSpeciality,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Specialities"],
    }),

    // get clinical diagram list
    getClinicalDiagrams: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getClinicalDiagramList}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["ClinicalDiagrams"],
    }),

    //update clinical diagram details
    updateClinicalDiagramDetails: builder.mutation<
      any,
      EditClinicalDiagramPayload
    >({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateClinicalDiagramDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["ClinicalDiagrams", "ClinicalDiagramDetails"],
    }),

    // get selected clinical diagram details
    getClinicalDiagramDetail: builder.query<ClinicalDiagram, string>({
      query: (id = "") => ({
        url: `${API_URL.getClinicalDiagramDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["ClinicalDiagramDetails"],
    }),

    // create clinical diagram
    createClinicalDiagram: builder.mutation<
      ClinicalDiagram,
      AddClinicalDiagramPayload
    >({
      query: (body) => ({
        url: API_URL.addClinicalDiagram,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["ClinicalDiagrams"],
    }),

    // get test types list
    getTestTypes: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getTestTypeList}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["TestTypes"],
    }),

    //update test type details
    updateTestTypeDetails: builder.mutation<any, EditTestTypePayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateTestTypeDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["TestTypes", "TestTypeDetails"],
    }),

    // get selected test type details
    getTestTypeDetail: builder.query<TestType, string>({
      query: (id = "") => ({
        url: `${API_URL.getTestTypeDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["TestTypeDetails"],
    }),

    // create test type
    createTestType: builder.mutation<TestType, AddTestTypePayload>({
      query: (body) => ({
        url: API_URL.addTestType,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["TestTypes"],
    }),

    // get procedures list
    getProcedures: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getProceduresList}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["Procedures"],
    }),

    //update procedure details
    updateProcedureDetails: builder.mutation<any, EditProcedurePayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.updateProcedureDetails}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Procedures", "ProcedureDetails"],
    }),

    // get selected procedure details
    getProcedureDetail: builder.query<Procedure, string>({
      query: (id = "") => ({
        url: `${API_URL.getProcedureDetails}/${id}`,
        method: "GET",
      }),
      providesTags: (_) => ["ProcedureDetails"],
    }),

    // create procedure
    createProcedure: builder.mutation<any, AddProcedurePayload>({
      query: (body) => ({
        url: API_URL.addProcedure,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Procedures"],
    }),

    // get Organs list
    getOrgans: builder.query<
      ListResponse<Organ>,
      Record<string, unknown> | undefined
    >({
      query: (query) => ({
        url: `${API_URL.organ}`,
        method: "GET",
        params: query ?? undefined,
      }),
      providesTags: (_) => ["Organs"],
    }),

    //update organ details
    updateOrganDetails: builder.mutation<any, EditOrganPayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.organ}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Organs"],
    }),

    // delete organ — hard delete, unlike the other reference-data entities
    // which are soft-deactivated via their update endpoint.
    deleteOrgan: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `${API_URL.organ}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Organs"],
    }),

    // create organ
    createOrgan: builder.mutation<Organ, AddOrganPayload>({
      query: (body) => ({
        url: API_URL.organ,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Organs"],
    }),

    // get Sites list
    getSites: builder.query<
      ListResponse<Site>,
      Record<string, unknown> | undefined
    >({
      query: (query) => ({
        url: `${API_URL.site}`,
        method: "GET",
        params: query ?? undefined,
      }),
      providesTags: (_) => ["Sites"],
    }),

    //update site details
    updateSiteDetails: builder.mutation<any, EditSitePayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.site}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Sites"],
    }),

    // delete site
    deleteSite: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `${API_URL.site}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Sites"],
    }),

    // create site
    createSite: builder.mutation<Site, AddSitePayload>({
      query: (body) => ({
        url: API_URL.site,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Sites"],
    }),

    // get Lesions list
    getLesions: builder.query<
      ListResponse<Lesion>,
      Record<string, unknown> | undefined
    >({
      query: (query) => ({
        url: `${API_URL.lesion}`,
        method: "GET",
        params: query ?? undefined,
      }),
      providesTags: (_) => ["Lesions"],
    }),

    //update lesion details
    updateLesionDetails: builder.mutation<any, EditLesionPayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.lesion}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Lesions"],
    }),

    // delete lesion
    deleteLesion: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `${API_URL.lesion}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Lesions"],
    }),

    // create lesion
    createLesion: builder.mutation<Lesion, AddLesionPayload>({
      query: (body) => ({
        url: API_URL.lesion,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Lesions"],
    }),

    // get MetadataList list
    getMetadataList: builder.query<
      ListResponse<Metadata>,
      Record<string, unknown> | undefined
    >({
      query: (query) => ({
        url: `${API_URL.metadata}`,
        method: "GET",
        params: query ?? undefined,
      }),
      providesTags: (_) => ["Metadata"],
    }),

    //update metadata details
    updateMetadataDetails: builder.mutation<any, EditMetadataPayload>({
      query: (body) => {
        const { id, ...rest } = body;
        return {
          url: `${API_URL.metadata}/${id}`,
          method: "PATCH",
          body: { ...rest },
        };
      },
      invalidatesTags: (_) => ["Metadata"],
    }),

    // delete metadata
    deleteMetadata: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `${API_URL.metadata}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Metadata"],
    }),

    // create metadata
    createMetadata: builder.mutation<Metadata, AddMetadataPayload>({
      query: (body) => ({
        url: API_URL.metadata,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Metadata"],
    }),

    //fetch s3 object
    fetchS3Object: builder.mutation<any, GetS3ObjectPayload>({
      query: (body) => ({
        url: API_URL.getS3Object,
        method: "POST",
        body,
      }),
      invalidatesTags: (_) => ["Attachments"],
    }),

    addDoctorDeliveryAddress: builder.mutation<
      AddDoctorDeliveryAddressResponse,
      {
        doctor_id: number;
        health_org_id?: number;
        is_associated?: boolean;
        address: DeliveryAddressPayload;
      }
    >({
      query: ({ doctor_id, health_org_id, is_associated = true, address }) => {
        const addressPayload = {
          phone: address.phone || undefined,
          provider_number: address.provider_number || undefined,
          fax: address.fax || undefined,
          mail: address.mail || undefined,
          unit_number: address.unit_number || undefined,
          street_number: address.street_number || undefined,
          street_name: address.street_name || undefined,
          suburb: address.suburb || undefined,
          state: address.state || undefined,
          postcode: address.postcode || undefined,
        };

        return {
          url: `${API_URL.doctorBase}/${doctor_id}/create/delivery_address`,
          method: "POST",
          body: {
            health_org_id: health_org_id,
            is_associated,
            address: addressPayload,
          },
        };
      },
      invalidatesTags: (_) => ["Surgeons", "SurgeonDetails"],
    }),
    removeDoctorFromHealthOrg: builder.mutation<
      any,
      { doctor_id: number; health_org_id: number }
    >({
      query: ({ doctor_id, health_org_id }) => ({
        url: `doctor/${doctor_id}/${API_URL.removeDoctorFromHealthOrg}?health_org_id=${health_org_id}`,
        method: "PATCH",
      }),
      invalidatesTags: (_) => ["Surgeons"],
    }),
    // hazard bag details by qr code
    getHazardBagByQr: builder.query<
      HazardBag,
      { healthOrgId: number; qrCodeNumber: string }
    >({
      query: ({ healthOrgId, qrCodeNumber }) => ({
        url: `hazard-bag/qr/${healthOrgId}/${qrCodeNumber}`,
        method: "GET",
      }),
      providesTags: (_) => ["HazardBag"],
    }),
    // get reference data
    getReferenceDataDetails: builder.query<any, any>({
      query: (query = "") => ({
        url: `${API_URL.getReferenceData}`,
        method: "GET",
        params: query,
      }),
      providesTags: (_) => ["ReferenceData"],
    }),
    //get api services based on user role
    getRoleAPIServices: builder.query<any, { userRoleId: number }>({
      query: ({ userRoleId }) => ({
        url: `${API_URL.getRoleAPIServiceList}/${userRoleId}`,
        method: "GET",
      }),
      providesTags: (_) => ["RoleAPIServices"],
    }),

    // specimen audit — activity feed / timeline events
    getSpecimenAuditEvents: builder.query<
      SpecimenAuditEventsResponse,
      SpecimenAuditEventsParams
    >({
      query: ({ healthOrgId, ...params }) => ({
        url: `admin/health-org/${healthOrgId}/${API_URL.getSpecimenAuditEvents}`,
        method: "GET",
        params,
      }),
      transformResponse: mapAuditEventsResponse,
      providesTags: (_) => ["SpecimenAudit"],
    }),

    // specimen audit — KPI / analytics band
    getSpecimenAuditAnalytics: builder.query<
      SpecimenAuditAnalyticsResponse,
      SpecimenAuditAnalyticsParams
    >({
      query: ({ healthOrgId, ...params }) => ({
        url: `admin/health-org/${healthOrgId}/${API_URL.getSpecimenAuditAnalytics}`,
        method: "GET",
        params,
      }),
      providesTags: (_) => ["SpecimenAudit"],
    }),
  }),
});

// Destructure individual mutation hooks from the 'api' object
export const {
  useLazyGetUserByMailQuery,
  useLoginMutation,
  useTwoFactorAuthMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useGetHealthOrganisationsQuery,
  useCreateHealthOrganisationMutation,
  useUpdateHealthOrganisationDetailsMutation,
  useGetHealthOrganisationDetailsQuery,
  useGetProfileDetailsQuery,
  useGetUserListQuery,
  useGetLaboratoriesQuery,
  useCreateLaboratoryMutation,
  useGetLaboratoryDetailsQuery,
  useUpdateLabDetailsMutation,
  useGetUserRolesQuery,
  useGetServicesQuery,
  useGetUserGroupQuery,
  useAddUserRoleMutation,
  useGetUserTypeQuery,
  useGetUserRoleDetailsQuery,
  useUpdateUserRoleStatusMutation,
  useUpdateUserRoleDetailsMutation,
  useCreateUserAdminMutation,
  useCreateUserMutation,
  useChangePasswordMutation,
  useLazyGetProfileDetailsQuery,
  useGetSurgeonsQuery,
  useGetSurgeonDetailsQuery,
  useUpdateSurgeonStatusMutation,
  useAddSurgeonMutation,
  useUpdateDoctorMutation,
  useUpdateSurgeonDefaultAddressMutation,
  useGetPatientSpecimenRecordsQuery,
  useGetPathologyRegisterPatientSpecimenRecordsQuery,
  useGetPatientRequestFormQuery,
  useGetPatientSpecimenRecordDetailsQuery,
  useGetPatientDetailsQuery,
  useGetRequestFormPdfQuery,
  useGetUsersListQuery,
  useUpdateUsersStatusMutation,
  useGetOperatingTheatreListQuery,
  useUpdateOperatingTheatreStatusMutation,
  useUpdateOperatingTheatreActivationMutation,
  useGetOperatingTheatreDetailsQuery,
  useAddOperatingTheatreMutation,
  useUpdateOperatingTheatreMutation,
  useAddUserMutation,
  useGetUserDetailsQuery,
  useUpdateUserMutation,
  useUpdateSurgeonMutation,
  useDeleteCookiesMutation,
  useGetSpecialitiesQuery,
  useGetSpecialityDetailQuery,
  useUpdateSpecialityDetailsMutation,
  useCreateSpecialityMutation,
  useGetClinicalDiagramsQuery,
  useUpdateClinicalDiagramDetailsMutation,
  useGetClinicalDiagramDetailQuery,
  useCreateClinicalDiagramMutation,
  useGetTestTypesQuery,
  useUpdateTestTypeDetailsMutation,
  useGetTestTypeDetailQuery,
  useCreateTestTypeMutation,
  useGetProceduresQuery,
  useUpdateProcedureDetailsMutation,
  useGetProcedureDetailQuery,
  useCreateProcedureMutation,
  useGetOrgansQuery,
  useUpdateOrganDetailsMutation,
  useCreateOrganMutation,
  useDeleteOrganMutation,
  useGetSitesQuery,
  useUpdateSiteDetailsMutation,
  useCreateSiteMutation,
  useDeleteSiteMutation,
  useGetLesionsQuery,
  useUpdateLesionDetailsMutation,
  useCreateLesionMutation,
  useDeleteLesionMutation,
  useGetMetadataListQuery,
  useUpdateMetadataDetailsMutation,
  useCreateMetadataMutation,
  useDeleteMetadataMutation,
  useFetchS3ObjectMutation,
  useAddDoctorDeliveryAddressMutation,
  useGetHazardBagByQrQuery,
  useLazyGetReferenceDataDetailsQuery,
  useAddDoctorToHealthOrgMutation,
  useRemoveDoctorFromHealthOrgMutation,
  useGetRoleAPIServicesQuery,
  useGetSpecimenAuditEventsQuery,
  useLazyGetSpecimenAuditEventsQuery,
  useGetSpecimenAuditAnalyticsQuery,
} = api;
